import _ from 'lodash';
import { isObservableMap, isObservableSet } from 'mobx';

/**
 * Returns a mutable plain-object snapshot of an instance whose properties
 * live as accessor descriptors on its prototype chain (e.g. a FilterParams
 * decorated with stage-3 `@observable accessor`). Use this anywhere you'd
 * reach for `_.clone(this.queryUtility.filter)`.
 *
 * Why this exists
 * ---------------
 * Under stage-3 `@observable accessor`, each declared field is a
 * non-enumerable getter/setter pair on the class prototype, backed by a
 * non-enumerable private field on the instance. The "standard" copy
 * mechanisms all observe the enumerability invariant and miss those:
 *
 *   - `_.clone(x)`, `_.cloneDeep(x)`, `_.toPlainObject(x)`,
 *     `Object.assign({}, x)`, `{...x}` — copy own enumerable properties
 *     only. Stage-3 accessors are non-enumerable, so none are copied.
 *     The result is an empty husk; reading any accessor on it throws
 *     `attempted to use private field on non-instance`.
 *
 *   - `mobx.toJS(x)` — works for `observable.object()` / `.array` /
 *     `.map`, but does not enumerate stage-3 `@observable accessor`
 *     fields on a class instance. Returns an empty plain object for
 *     FilterParams (verified empirically).
 *
 *   - `JSON.parse(JSON.stringify(x))` — would require a `toJSON` method
 *     on every class, since JSON.stringify also only touches own
 *     enumerable properties.
 *
 *   - `serializr` from the MobX ecosystem — works, but requires schema
 *     declarations on every class.
 *
 * The only language facilities that bypass enumerability are
 * `Object.getOwnPropertyDescriptors(...)` and `Reflect.ownKeys(...)`.
 * `cloneAccessors` walks the prototype chain with those, invokes each
 * accessor getter on the live instance, and writes the value onto a
 * plain object. The result is safe to mutate and safe to feed to
 * `findParams` / `uriTemplate.expand` downstream.
 */
export function extractAccessors(source) {
    if (!_.isObject(source)) return source;
    const result = {};
    for (let proto = Object.getPrototypeOf(source); proto && proto !== Object.prototype; proto = Object.getPrototypeOf(proto)) {
        const descs = Object.getOwnPropertyDescriptors(proto);
        for (const key of Object.keys(descs)) {
            if (key === 'constructor') continue;
            const d = descs[key];
            if (d.get || d.set) {
                result[key] = source[key];
            }
        }
    }
    _.assign(result, source);
    return result;
}

/**
 * The "deconstruct accessors" interface.
 *
 * A class whose fields are stage-3 `@observable accessor` declarations can
 * implement a `deconstructAccessors()` method to control exactly how one of its
 * instances is extracted into a (shallow) plain object. Both clone helpers
 * below — `cloneAccessors` and `deepCloneAccessors` — delegate to that method
 * when it exists; otherwise they fall back to the default `extractAccessors`
 * prototype-walk. This lets a model (e.g. `FilterParams`) own and customize its
 * own deconstruction without changing the generic utility.
 *
 * NOTE: an implementer's `deconstructAccessors()` must build its plain object
 * with `extractAccessors(this)` (the NON-delegating primitive), not with
 * `cloneAccessors`/`deepCloneAccessors`, or it would recurse into itself.
 */

/**
 * Shallow clone of an accessor instance into a mutable plain object. Delegates
 * to the source's own `deconstructAccessors()` method when present, otherwise
 * uses the default `extractAccessors` walk. Safe to mutate and safe to feed to
 * `findParams` / `uriTemplate.expand` downstream.
 */
export function cloneAccessors(source) {
    if (!_.isObject(source)) return source;
    if (typeof source.deconstructAccessors === 'function') {
        return source.deconstructAccessors();
    }
    return extractAccessors(source);
}

/**
 * Recursive deconstruction that unpacks stage-3 `@observable accessor` instances
 * at EVERY nesting level into a fully detached plain tree.
 *
 * For each object node it extracts a shallow plain object — via the node's own
 * `deconstructAccessors()` method if it implements one, otherwise via the
 * default `extractAccessors` prototype-walk — then recurses into the extracted
 * values. Where a single `extractAccessors` materializes only the top-level
 * accessor fields (nested accessor instances stay in their non-enumerable form,
 * so a later `_.cloneDeep`/spread loses them), this walks the chain at each
 * node, so an embedded sub-filter becomes a plain object too.
 *
 * Arrays, Maps, Sets and Dates are copied (not shared by reference), and MobX
 * observable collections become plain JS equivalents. A WeakMap guards cycles.
 */
export function deepCloneAccessors(source, seen = new WeakMap()) {
    if (!_.isObject(source) || _.isFunction(source)) return source;
    if (seen.has(source)) return seen.get(source);

    if (source instanceof Date) return new Date(source.getTime());

    if (Array.isArray(source)) {
        const arr = [];
        seen.set(source, arr);
        source.forEach(value => arr.push(deepCloneAccessors(value, seen)));
        return arr;
    }

    if (source instanceof Map || isObservableMap(source)) {
        const map = new Map();
        seen.set(source, map);
        source.forEach((value, key) => map.set(key, deepCloneAccessors(value, seen)));
        return map;
    }

    if (source instanceof Set || isObservableSet(source)) {
        const set = new Set();
        seen.set(source, set);
        source.forEach(value => set.add(deepCloneAccessors(value, seen)));
        return set;
    }

    // Any other object — a plain object OR an accessor/class instance. Extract a
    // shallow plain object via the object's own deconstructAccessors() method (if
    // implemented) or the default accessor walk, then recurse into its values.
    const result = {};
    seen.set(source, result);

    const extracted = typeof source.deconstructAccessors === 'function'
        ? source.deconstructAccessors()
        : extractAccessors(source);

    for (const key of Object.keys(extracted)) {
        result[key] = deepCloneAccessors(extracted[key], seen);
    }

    return result;
}
